import React, { useState, useEffect } from 'react';
import { Plus, Loader2, Users, ChevronDown, ChevronUp, Pencil, X, Save, KeyRound, Search, Filter, ArrowUpDown, Trash2, Download, FileText, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Visionary } from '../types/visionary';
import type { Order } from '../types/orders';
import { OrderStatus } from './Warehouse';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

type SortableFields = 'membership_number' | 'name' | 'email' | 'loyalty_points';

// Define a custom OrderItem interface specifically for this component
interface OrderItemWithProducts {
  id: string;
  product_id: number;
  quantity: number;
  price_at_time: number;
  products: {
    name: string;
    platform?: string;
    image_url: string;
  };
}

// Extend the Order interface to include status
interface OrderWithStatus extends Order {
  status: OrderStatus;
}

// Update Visionary type to include login_id and allow string for loyalty_points during editing
interface VisionaryWithLogin extends Omit<Visionary, 'loyalty_points'> {
  login_id?: number;
  loyalty_points: number | string; // Allow string during editing
}

export default function Visionaries() {
  const { user } = useAuth();
  const [visionaries, setVisionaries] = useState<VisionaryWithLogin[]>([]);
  const [filteredVisionaries, setFilteredVisionaries] = useState<VisionaryWithLogin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedVisionary, setExpandedVisionary] = useState<number | null>(null);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [orders, setOrders] = useState<Record<number, OrderWithStatus[]>>({});
  const [orderItems, setOrderItems] = useState<Record<string, OrderItemWithProducts[]>>({});
  const [editingVisionary, setEditingVisionary] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{ key: SortableFields; direction: 'asc' | 'desc' } | null>(null);
  const itemsPerPage = 10;
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    address: '',
    telephone: '',
    password: ''
  });
  const [editData, setEditData] = useState<VisionaryWithLogin | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedVisionary, setSelectedVisionary] = useState<VisionaryWithLogin | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  
  // GDPR Deletion state
  const [showGdprModal, setShowGdprModal] = useState(false);
  const [gdprVisionary, setGdprVisionary] = useState<VisionaryWithLogin | null>(null);
  const [isExportingData, setIsExportingData] = useState(false);
  const [isAnonymizing, setIsAnonymizing] = useState(false);

  useEffect(() => {
    fetchVisionaries();
  }, []);

  useEffect(() => {
    const filtered = visionaries.filter(visionary => 
      visionary.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      visionary.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      visionary.membership_number.toString().includes(searchQuery)
    );
    setFilteredVisionaries(filtered);
    setCurrentPage(1);
  }, [searchQuery, visionaries]);

  const fetchVisionaries = async () => {
    try {
      const { data, error } = await supabase
        .from('visionaries')
        .select('*, login_id')
        .order('membership_number');

      if (error) throw error;
      setVisionaries(data || []);
    } catch (err) {
      console.error('Error fetching visionaries:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisionaryOrders = async (membershipNumber: number) => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*, status')
        .eq('visionary_id', membershipNumber)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(prev => ({ ...prev, [membershipNumber]: data || [] }));
    } catch (err) {
      console.error('Error fetching orders:', err);
    }
  };

  const fetchOrderItems = async (orderId: string) => {
    try {
      const { data: items, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          id,
          product_id,
          quantity,
          price_at_time,
          products:products!inner(name, platform, image_url)
        `)
        .eq('order_id', orderId)
        .returns<{
          id: string;
          product_id: number;
          quantity: number;
          price_at_time: number;
          products: {
            name: string;
            platform?: string;
            image_url: string;
          };
        }[]>();

      if (itemsError) throw itemsError;

      const formattedItems: OrderItemWithProducts[] = items.map(item => ({
        id: item.id,
        product_id: item.product_id,
        quantity: item.quantity,
        price_at_time: item.price_at_time,
        products: {
          name: item.products.name,
          platform: item.products.platform,
          image_url: item.products.image_url
        }
      }));

      setOrderItems(prev => ({ ...prev, [orderId]: formattedItems }));
    } catch (err) {
      console.error('Error fetching order items:', err);
    }
  };

  const handleExpandVisionary = async (membershipNumber: number) => {
    if (expandedVisionary === membershipNumber) {
      setExpandedVisionary(null);
      setExpandedOrder(null);
    } else {
      setExpandedVisionary(membershipNumber);
      if (!orders[membershipNumber]) {
        await fetchVisionaryOrders(membershipNumber);
      }
    }
  };

  const handleExpandOrder = async (orderId: string) => {
    if (expandedOrder === orderId) {
      setExpandedOrder(null);
    } else {
      setExpandedOrder(orderId);
      if (!orderItems[orderId]) {
        await fetchOrderItems(orderId);
      }
    }
  };

  const startEditing = (visionary: VisionaryWithLogin) => {
    setEditingVisionary(visionary.membership_number);
    setEditData(visionary);
  };

  const cancelEditing = () => {
    setEditingVisionary(null);
    setEditData(null);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editData) return;
    
    let value = e.target.value;
    // Add telephone number formatting to allow only digits
    if (e.target.name === 'telephone') {
      value = value.replace(/\D/g, '');
    }
    
    // For loyalty_points, convert to number but allow empty string during editing
    if (e.target.name === 'loyalty_points') {
      setEditData({
        ...editData,
        [e.target.name]: value === '' ? '' : parseInt(value) || 0
      });
    } else {
      setEditData({
        ...editData,
        [e.target.name]: value
      });
    }
  };

  const saveChanges = async (membershipNumber: number) => {
    if (!editData) return;
    
    // Add telephone number length validation
    const MIN_TELEPHONE_LENGTH = 7;
    if (editData.telephone && editData.telephone.length < MIN_TELEPHONE_LENGTH) {
      alert(`Telephone number must be at least ${MIN_TELEPHONE_LENGTH} digits.`);
      return;
    }
    
    try {
      // Use the RPC function instead of direct table update
      const { error } = await supabase
        .rpc('update_visionary_details', {
          p_membership_number: membershipNumber,
          p_name: editData.name,
          p_email: editData.email,
          p_address: editData.address,
          p_telephone: editData.telephone,
          p_loyalty_points: typeof editData.loyalty_points === 'string' 
            ? parseInt(editData.loyalty_points) || 0 
            : editData.loyalty_points
        });

      if (error) throw error;

      setVisionaries(prev =>
        prev.map(v =>
          v.membership_number === membershipNumber ? editData : v
        )
      );
      setEditingVisionary(null);
      setEditData(null);
    } catch (err) {
      console.error('Error updating visionary:', err);
      alert('Failed to update visionary details');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    // Add telephone number length validation
    const MIN_TELEPHONE_LENGTH = 7;
    if (formData.telephone && formData.telephone.length < MIN_TELEPHONE_LENGTH) {
      setError(`Telephone number must be at least ${MIN_TELEPHONE_LENGTH} digits.`);
      setSaving(false);
      return;
    }

    // Add password length validation
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setSaving(false);
      return;
    }

    try {
      // Create login for the visionary with the entered password
      const { data: loginData, error: loginError } = await supabase
        .rpc('create_login', {
          p_username: formData.email,
          p_password: formData.password, // Use the entered password instead of hardcoded one
          p_role: 'visionary'
        });

      if (loginError) throw loginError;

      // Create visionary record with login_id
      const { data, error } = await supabase
        .from('visionaries')
        .insert([{
          email: formData.email,
          name: formData.name,
          address: formData.address,
          telephone: formData.telephone,
          login_id: loginData.id,
          loyalty_points: 0
        }])
        .select()
        .single();

      if (error) throw error;

      setVisionaries(prev => [...prev, data]);
      setShowAddModal(false);
      setFormData({ email: '', name: '', address: '', telephone: '', password: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let value = e.target.value;
    // Add telephone number formatting to allow only digits
    if (e.target.name === 'telephone') {
      value = value.replace(/\D/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [e.target.name]: value
    }));
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVisionary || !selectedVisionary.login_id || !newPassword) return;
    
    setResetting(true);
    try {
      // Use the RPC function to update visionary password
      const { data, error } = await supabase.rpc('update_visionary_password', {
        p_email: selectedVisionary.email,
        p_new_password: newPassword
      });
      
      if (error) {
        console.error('RPC error:', error);
        throw error;
      }

      if (!data) {
        throw new Error('No visionary found with this email address');
      }

      console.log('Password reset successful');
      
      setShowResetPasswordModal(false);
      setNewPassword('');
      alert('Password has been reset successfully');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(`Failed to reset password: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setResetting(false);
    }
  };

  const openResetPasswordModal = (visionary: VisionaryWithLogin) => {
    setSelectedVisionary(visionary);
    setShowResetPasswordModal(true);
  };

  const handleSort = (key: SortableFields) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredVisionaries].sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setFilteredVisionaries(sorted);
  };

  const totalPages = Math.ceil(filteredVisionaries.length / itemsPerPage);
  const paginatedVisionaries = filteredVisionaries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const stats = {
    total: visionaries.length
  };

  // Get status badge style based on order status
  const getStatusBadgeStyle = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PROCESSING:
        return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case OrderStatus.DISPATCHED:
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case OrderStatus.COMPLETE:
        return 'bg-green-700/20 text-green-400 border-green-700/30';
      case OrderStatus.CANCELLED:
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case OrderStatus.RETURN_INITIATED:
        return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case OrderStatus.REFUNDED:
        return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  // GDPR Export Function
  const exportGdprData = async (visionary: VisionaryWithLogin) => {
    setIsExportingData(true);
    try {
      // Try to get comprehensive data from RPC function first
      let gdprData = null;
      let useRpcData = false;
      
      try {
        const { data, error } = await supabase.rpc('get_visionary_gdpr_data', {
          p_membership_number: visionary.membership_number
        });
        
        if (!error && data) {
          gdprData = data;
          useRpcData = true;
        }
      } catch (rpcError) {
        console.log('RPC function not available, using fallback method');
      }

      // Fallback: Get orders data from frontend state or fetch directly
      let visionaryOrders = orders[visionary.membership_number] || [];
      if (visionaryOrders.length === 0) {
        // Try to fetch orders if not already loaded
        try {
          const { data: ordersData } = await supabase
            .from('orders')
            .select('*, status')
            .eq('visionary_id', visionary.membership_number)
            .order('created_at', { ascending: false });
          visionaryOrders = ordersData || [];
        } catch (orderError) {
          console.log('Could not fetch orders data');
        }
      }

      // Create PDF
      const pdf = new jsPDF();
      
      // Title
      pdf.setFontSize(20);
      pdf.text('GDPR Data Export', 20, 30);
      
      // Export info
      pdf.setFontSize(12);
      pdf.text(`Export Date: ${new Date().toLocaleString()}`, 20, 45);
      pdf.text(`Data Subject: ${visionary.name}`, 20, 55);
      pdf.text(`Membership Number: ${visionary.membership_number}`, 20, 65);
      
      // Visionary Details Section
      pdf.setFontSize(16);
      pdf.text('Personal Information', 20, 85);
      
      const visionaryData = [
        ['Field', 'Value'],
        ['Name', visionary.name],
        ['Email', visionary.email],
        ['Address', visionary.address],
        ['Telephone', visionary.telephone],
        ['Loyalty Points', visionary.loyalty_points.toString()],
        ['Membership Number', visionary.membership_number.toString()]
      ];

      autoTable(pdf, {
        head: [visionaryData[0]],
        body: visionaryData.slice(1),
        startY: 95,
        theme: 'grid',
        headStyles: { fillColor: [128, 90, 213] }
      });

      // Orders Section
      if (useRpcData && gdprData?.orders && gdprData.orders.length > 0) {
        // Use comprehensive RPC data
        const finalY = (pdf as any).lastAutoTable.finalY || 95;
        
        pdf.setFontSize(16);
        pdf.text('Order History (Comprehensive)', 20, finalY + 20);
        
        const orderHeaders = ['Order ID', 'Date', 'Total', 'Points Used', 'Points Earned', 'Status'];
        const orderRows = gdprData.orders.map((order: any) => [
          order.order_id,
          new Date(order.created_at).toLocaleDateString(),
          `£${order.total_amount.toFixed(2)}`,
          order.points_used.toString(),
          order.points_earned.toString(),
          order.status
        ]);

        autoTable(pdf, {
          head: [orderHeaders],
          body: orderRows,
          startY: finalY + 30,
          theme: 'grid',
          headStyles: { fillColor: [128, 90, 213] }
        });

        // Order Items Details
        let currentY = (pdf as any).lastAutoTable.finalY + 20;
        
        gdprData.orders.forEach((order: any) => {
          if (currentY > 250) {
            pdf.addPage();
            currentY = 30;
          }
          
          pdf.setFontSize(14);
          pdf.text(`Order ${order.order_id} - Items`, 20, currentY);
          
          if (order.items && order.items.length > 0) {
            const itemHeaders = ['Product', 'Platform', 'Quantity', 'Price'];
            const itemRows = order.items.map((item: any) => [
              item.product_name,
              item.platform || 'N/A',
              item.quantity.toString(),
              `£${item.price_at_time.toFixed(2)}`
            ]);

            autoTable(pdf, {
              head: [itemHeaders],
              body: itemRows,
              startY: currentY + 10,
              theme: 'grid',
              headStyles: { fillColor: [79, 70, 229] }
            });
            
            currentY = (pdf as any).lastAutoTable.finalY + 20;
          } else {
            pdf.setFontSize(10);
            pdf.text('No items found for this order', 20, currentY + 10);
            currentY += 20;
          }
        });
      } else if (visionaryOrders.length > 0) {
        // Use basic orders data from frontend
        const finalY = (pdf as any).lastAutoTable.finalY || 95;
        
        pdf.setFontSize(16);
        pdf.text('Order History (Basic)', 20, finalY + 20);
        
        const orderHeaders = ['Order ID', 'Date', 'Total', 'Points Used', 'Points Earned', 'Status'];
        const orderRows = visionaryOrders.map((order: any) => [
          order.id,
          new Date(order.created_at).toLocaleDateString(),
          `£${order.total_amount.toFixed(2)}`,
          order.points_used?.toString() || '0',
          order.points_earned?.toString() || '0',
          order.status || 'Unknown'
        ]);

        autoTable(pdf, {
          head: [orderHeaders],
          body: orderRows,
          startY: finalY + 30,
          theme: 'grid',
          headStyles: { fillColor: [128, 90, 213] }
        });
      } else {
        // No orders found
        const finalY = (pdf as any).lastAutoTable.finalY || 95;
        pdf.setFontSize(14);
        pdf.text('No order history found', 20, finalY + 30);
      }

      // Footer
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.text(`Page ${i} of ${pageCount}`, 20, pdf.internal.pageSize.height - 10);
        pdf.text('This document contains personal data processed under GDPR', 100, pdf.internal.pageSize.height - 10);
      }

      // Download the PDF
      pdf.save(`GDPR_Export_${visionary.name.replace(/\s+/g, '_')}_${Date.now()}.pdf`);
      
    } catch (err) {
      console.error('Error exporting GDPR data:', err);
      alert(`Failed to export data: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsExportingData(false);
    }
  };

  // Anonymize Account Function
  const anonymizeAccount = async (visionary: VisionaryWithLogin) => {
    if (!user?.username) {
      alert('Unable to identify staff member for this operation');
      return;
    }

    setIsAnonymizing(true);
    try {
      console.log('Attempting to anonymize account:', {
        membership_number: visionary.membership_number,
        staff_email: user.username
      });

      const { data, error } = await supabase.rpc('anonymize_visionary_account', {
        p_membership_number: visionary.membership_number,
        p_staff_email: user.username
      });

      console.log('RPC Response:', { data, error });

      if (error) {
        console.error('Supabase RPC Error:', error);
        throw new Error(error.message || error.details || JSON.stringify(error));
      }

      if (!data) {
        throw new Error('No response data from anonymization function');
      }

      if (!data.success) {
        throw new Error(data.message || 'Anonymization function returned failure');
      }

      // Update local state to reflect anonymised data
      const timestamp = Date.now();
      setVisionaries(prev => 
        prev.map(v => 
          v.membership_number === visionary.membership_number 
            ? {
                ...v,
                name: `Anonymised User ${timestamp}`,
                email: `anonymised${timestamp}@gdpr.deleted`,
                address: 'Address Anonymised',
                telephone: '0000000000'
              }
            : v
        )
      );

      setShowGdprModal(false);
      setGdprVisionary(null);
      alert('Account has been successfully anonymised in compliance with GDPR');
      
    } catch (err) {
      console.error('Error anonymizing account:', err);
      
      let errorMessage = 'Unknown error occurred';
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err && typeof err === 'object') {
        errorMessage = JSON.stringify(err);
      }
      
      alert(`Failed to anonymize account: ${errorMessage}`);
    } finally {
      setIsAnonymizing(false);
    }
  };

  // Open GDPR modal
  const openGdprModal = (visionary: VisionaryWithLogin) => {
    setGdprVisionary(visionary);
    setShowGdprModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 overflow-y-auto">
      <div className="max-w-[90%] mx-auto p-8">
        <div className="space-y-6">
          {/* Header with Stats */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-500/20 p-2 rounded-lg">
                <Users className="w-8 h-8 text-purple-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Visionaries</h1>
                <p className="text-white/70 text-sm">{stats.total.toLocaleString()} registered members</p>
              </div>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white rounded-lg transition-all duration-200 flex items-center gap-2 shadow-lg shadow-purple-900/20 font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Visionary
            </button>
          </div>

          {/* Search */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-white/50 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by name, email, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all duration-200"
              />
            </div>
          </div>

          {/* Visionaries Table */}
          <div className="bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">
                      <button
                        onClick={() => handleSort('membership_number')}
                        className="flex items-center gap-2 hover:text-purple-400 transition-colors group"
                      >
                        ID
                        <ArrowUpDown className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-2 hover:text-purple-400 transition-colors group"
                      >
                        Name
                        <ArrowUpDown className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">
                      <button
                        onClick={() => handleSort('email')}
                        className="flex items-center gap-2 hover:text-purple-400 transition-colors group"
                      >
                        Email
                        <ArrowUpDown className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Address</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Telephone</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">
                      <button
                        onClick={() => handleSort('loyalty_points')}
                        className="flex items-center gap-2 hover:text-purple-400 transition-colors group"
                      >
                        Points
                        <ArrowUpDown className="w-4 h-4 opacity-50 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {paginatedVisionaries.map((visionary) => (
                    <React.Fragment key={visionary.membership_number}>
                      <tr className="group hover:bg-white/5 transition-all duration-200">
                        <td className="px-6 py-4 text-sm">
                          <span className="text-purple-400 font-medium">#{visionary.membership_number}</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-white">
                          {editingVisionary === visionary.membership_number ? (
                            <input
                              type="text"
                              name="name"
                              value={editData?.name || ''}
                              onChange={handleEditChange}
                              className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                            />
                          ) : (
                            <span className="font-medium">{visionary.name}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-white/70">
                          {editingVisionary === visionary.membership_number ? (
                            <input
                              type="email"
                              name="email"
                              value={editData?.email || ''}
                              onChange={handleEditChange}
                              className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                            />
                          ) : (
                            visionary.email
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-white/70">
                          {editingVisionary === visionary.membership_number ? (
                            <input
                              type="text"
                              name="address"
                              value={editData?.address || ''}
                              onChange={handleEditChange}
                              className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                            />
                          ) : (
                            visionary.address
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-white/70">
                          {editingVisionary === visionary.membership_number ? (
                            <input
                              type="tel"
                              name="telephone"
                              value={editData?.telephone || ''}
                              onChange={handleEditChange}
                              className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                            />
                          ) : (
                            visionary.telephone
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {editingVisionary === visionary.membership_number ? (
                            <input
                              type="number"
                              name="loyalty_points"
                              value={editData?.loyalty_points === '' ? '' : editData?.loyalty_points || 0}
                              onChange={handleEditChange}
                              className="w-full px-2 py-1 bg-white/10 border border-white/20 rounded text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                            />
                          ) : (
                            <span className="text-white font-medium">{visionary.loyalty_points.toLocaleString()}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                            {editingVisionary === visionary.membership_number ? (
                              <>
                                <button
                                  onClick={() => saveChanges(visionary.membership_number)}
                                  className="p-1.5 hover:bg-green-500/20 rounded-lg transition-colors text-green-400"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => startEditing(visionary)}
                                  className="p-1.5 hover:bg-purple-500/20 rounded-lg transition-colors text-white"
                                  title="Edit details"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => openResetPasswordModal(visionary)}
                                  className="p-1.5 hover:bg-purple-500/20 rounded-lg transition-colors text-white"
                                  title="Reset password"
                                >
                                  <KeyRound className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleExpandVisionary(visionary.membership_number)}
                                  className="p-1.5 hover:bg-purple-500/20 rounded-lg transition-colors text-white"
                                  title="View orders"
                                >
                                  {expandedVisionary === visionary.membership_number ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => openGdprModal(visionary)}
                                  className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                                  title="GDPR Deletion"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedVisionary === visionary.membership_number && (
                        <tr>
                          <td colSpan={7} className="px-6 py-4 bg-purple-500/5">
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <h3 className="text-lg font-medium text-white">Order History</h3>
                                <span className="text-sm text-white/70">
                                  {orders[visionary.membership_number]?.length || 0} orders
                                </span>
                              </div>
                              {orders[visionary.membership_number]?.length > 0 ? (
                                <div className="grid gap-4">
                                  {orders[visionary.membership_number].map((order) => (
                                    <div key={order.id} className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-all duration-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <div>
                                          <p className="text-white font-medium">
                                            Order #{order.id}
                                          </p>
                                          <p className="text-white/70 text-sm">
                                            {new Date(order.created_at).toLocaleDateString()}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className={`px-2 py-1 rounded-full text-xs border ${getStatusBadgeStyle(order.status)}`}>
                                            {order.status}
                                          </span>
                                          <button
                                            onClick={() => handleExpandOrder(order.id)}
                                            className="p-1 hover:bg-white/10 rounded transition-colors"
                                          >
                                            {expandedOrder === order.id ? (
                                              <ChevronUp className="w-4 h-4 text-white" />
                                            ) : (
                                              <ChevronDown className="w-4 h-4 text-white" />
                                            )}
                                          </button>
                                        </div>
                                      </div>
                                      {expandedOrder === order.id && orderItems[order.id] && (
                                        <div className="mt-4 space-y-2">
                                          {orderItems[order.id].map((item) => (
                                            <div
                                              key={item.id}
                                              className="flex justify-between text-white/80 text-sm"
                                            >
                                              <span>{item.products.name} × {item.quantity}</span>
                                              <span>£{item.price_at_time.toFixed(2)}</span>
                                            </div>
                                          ))}
                                          <div className="pt-2 mt-2 border-t border-white/10">
                                            <div className="flex justify-between text-white">
                                              <span>Total</span>
                                              <span>£{order.total_amount.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between text-white/70 text-sm">
                                              <span>Points Used</span>
                                              <span>{order.points_used}</span>
                                            </div>
                                            <div className="flex justify-between text-white/70 text-sm">
                                              <span>Points Earned</span>
                                              <span>{order.points_earned}</span>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-center py-8 text-white/70">
                                  <p>No orders found for this visionary</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-white/10 bg-white/5">
                <div className="text-sm text-white/70">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredVisionaries.length)} of {filteredVisionaries.length} entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 rounded-lg transition-all duration-200 text-sm font-medium ${
                        currentPage === page
                          ? 'bg-purple-600 text-white'
                          : 'bg-white/10 hover:bg-white/20 text-white'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Add Visionary Modal */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 w-full max-w-md">
                <h2 className="text-xl font-semibold text-white mb-4">Add New Visionary</h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="p-3 text-sm text-red-200 bg-red-500/20 border border-red-500/20 rounded-lg">
                      {error}
                    </div>
                  )}
                  
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-white/50"
                      placeholder="Enter name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-white/50"
                      placeholder="Enter email"
                    />
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-white mb-1">
                      Address
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      rows={3}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-white/50"
                      placeholder="Enter address"
                    />
                  </div>

                  <div>
                    <label htmlFor="telephone" className="block text-sm font-medium text-white mb-1">
                      Telephone
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-white/50"
                      placeholder="Enter telephone"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-white mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-white/50"
                      placeholder="Enter password"
                    />
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors flex items-center gap-2"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          Add Visionary
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Reset Password Modal */}
          {showResetPasswordModal && selectedVisionary && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 w-full max-w-md">
                <h2 className="text-xl font-semibold text-white mb-4">Reset Password</h2>
                <p className="text-white/70 mb-4">
                  Reset password for {selectedVisionary.name} ({selectedVisionary.email})
                </p>
                
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-white mb-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white"
                      placeholder="Enter new password"
                      minLength={8}
                    />
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowResetPasswordModal(false);
                        setNewPassword('');
                        setSelectedVisionary(null);
                      }}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={resetting}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors flex items-center gap-2"
                    >
                      {resetting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <KeyRound className="w-4 h-4" />
                          Reset Password
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* GDPR Modal */}
          {showGdprModal && gdprVisionary && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 w-full max-w-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-red-500/20 p-2 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">GDPR Data Management</h2>
                    <p className="text-sm text-white/70">Data Subject Rights Request</p>
                  </div>
                </div>
                
                <div className="bg-white/5 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-purple-400 font-medium">#{gdprVisionary.membership_number}</span>
                    <span className="text-white font-medium">{gdprVisionary.name}</span>
                  </div>
                  <p className="text-white/70 text-sm">{gdprVisionary.email}</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-blue-400" />
                      <h3 className="text-white font-medium">Export Data (Article 15)</h3>
                    </div>
                    <p className="text-white/70 text-sm">
                      Generate a comprehensive PDF containing all personal data, order history, and transaction details for this visionary.
                    </p>
                    <button
                      onClick={() => exportGdprData(gdprVisionary)}
                      disabled={isExportingData}
                      className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg transition-colors flex items-center gap-2 text-sm"
                    >
                      {isExportingData ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating PDF...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4" />
                          Export GDPR Data
                        </>
                      )}
                    </button>
                  </div>

                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Trash2 className="w-5 h-5 text-red-400" />
                      <h3 className="text-white font-medium">Anonymize Account (Article 17)</h3>
                    </div>
                    <p className="text-white/70 text-sm mb-3">
                      <strong>⚠️ WARNING:</strong> This action will permanently anonymize personal data (name, email, address, phone). 
                      Membership number and order history will be preserved for business analytics.
                    </p>
                    <div className="bg-red-500/10 rounded p-3 mb-3">
                      <p className="text-red-300 text-xs">
                        • Name will become "Anonymised User"<br/>
                        • Email will become "anonymised@gdpr.deleted"<br/>
                        • Address and phone will be cleared<br/>
                        • Login access will be disabled<br/>
                        • <strong>This action cannot be undone</strong>
                      </p>
                    </div>
                    <button
                      onClick={() => anonymizeAccount(gdprVisionary)}
                      disabled={isAnonymizing}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white rounded-lg transition-colors flex items-center gap-2 text-sm"
                    >
                      {isAnonymizing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Anonymizing...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Anonymize Account
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShowGdprModal(false);
                      setGdprVisionary(null);
                    }}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}